import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Dropdown, ListView, SearchBar } from '@commons/utils/styledLibraryComponents';
import { haveSameIds, mergeReportedSelection } from '@commons/utils/listViewSelection';
import EmptyState from '@commons/EmptyState';

import { externalClient as externalClientService } from '@services/externalClient';

import { getCentralKitchenStores, getSalesPointStores } from '@selectors/stores';

import normalizeStringValue from '@commons/utils/normalizeStringValue';

import {
  CATEGORY_FILTER_ICON,
  CENTRAL_FILTER_ICON,
  getCentralProductFilterItems,
  SUB_CATEGORY_FILTER_ICON,
} from '../../utils/catalogFilters';
import { getCatalogProductColumns } from '../../utils/getCatalogProductColumns';

import { Container, HeaderContainer } from './styledComponents';

const DEFAULT_MAX_PER_PAGE = 100;

// Id pinned first by the Dropdown (its mandatory-first item), used for the "Aucune" filter option
// (product category / sub-category is null/empty). It doubles as the raw filter value.
const NONE_FILTER_ID = -1;

/**
 * Distinct filter options for a category-like property. The uncategorized bucket uses the pinned
 * "Aucune" option (id NONE_FILTER_ID, kept first by the Dropdown). The option id doubles as the raw
 * filter value (the sentinel for null), so the selection maps back to products without a lookup.
 */
const getCategoryFilterItems = (products, propertyKey) => {
  const itemsById = new Map();

  products.forEach((product) => {
    const rawValue = product[propertyKey];
    const id = rawValue || NONE_FILTER_ID;

    if (!itemsById.has(id)) {
      itemsById.set(id, { id, value: rawValue || i18next.t('GENERAL.NONE_VALUE') });
    }
  });

  return Array.from(itemsById.values());
};

/**
 * Decoupled manual product picker: the client's selectable products (multi-select) for the catalog.
 * Kept free of any modal knowledge so it can be dropped into a standalone page later; it only
 * reports the selected product ids via onSelectionChange. The list is fetched once for the given
 * store perimeter (the API returns the full selectable set, so search / paging stay client-side);
 * passing `externalClientId` excludes the products already in that client's catalog, which is what
 * the catalog-administration "Ajouter" modal relies on.
 * Search + category / sub-category / central-product filters narrow the list client-side; every
 * filter is required, so at least one option always stays selected. The central-product filter is
 * only offered when the external client is associated to central kitchens *and* to sales points,
 * since it cannot tell anything apart otherwise.
 */
const ManualProductList = (props) => {
  const { stores, externalClientId, hasCentralKitchens, selectedProductIds, onSelectionChange } =
    props;

  const storeIds = useMemo(() => stores.map(({ id }) => id), [stores]);

  // Only an external client associated to both kinds of stores can have central and non-central
  // products to tell apart: otherwise the filter would either keep everything or empty the list
  const displayCentralFilter =
    !!getCentralKitchenStores(stores).length && !!getSalesPointStores(stores).length;

  const listViewRef = useRef(null);
  const [columns] = useState(getCatalogProductColumns(hasCentralKitchens));

  const [products, setProducts] = useState([]);
  // Starts true so the very first render shows the loading state instead of a "no result" flash.
  const [isLoading, setIsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [selectedCategoryItems, setSelectedCategoryItems] = useState([]);
  const [selectedSubCategoryItems, setSelectedSubCategoryItems] = useState([]);
  const [centralFilterItems] = useState(getCentralProductFilterItems);
  const [selectedCentralItems, setSelectedCentralItems] = useState(getCentralProductFilterItems);

  const storeIdsKey = storeIds.join(',');

  useEffect(() => {
    const fetchProducts = async () => {
      if (!storeIds.length) {
        setProducts([]);
        setIsLoading(false);

        return;
      }

      setIsLoading(true);

      try {
        const data = await externalClientService.getSelectableProducts({
          storeIds,
          externalClientId,
        });

        setProducts(data || []);
      } catch {
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [storeIdsKey, externalClientId]);

  const categoryItems = useMemo(() => getCategoryFilterItems(products, 'category'), [products]);
  const subCategoryItems = useMemo(
    () => getCategoryFilterItems(products, 'subCategory'),
    [products],
  );

  // Reset the category / sub-category selections to "all" whenever the option set changes (i.e. a
  // new store perimeter was fetched), so nothing is filtered out by default.
  useEffect(() => {
    setSelectedCategoryItems(categoryItems);
  }, [categoryItems]);

  useEffect(() => {
    setSelectedSubCategoryItems(subCategoryItems);
  }, [subCategoryItems]);

  const filteredProducts = useMemo(() => {
    const selectedCategoryIds = selectedCategoryItems.map(({ id }) => id);
    const selectedSubCategoryIds = selectedSubCategoryItems.map(({ id }) => id);
    const selectedKitchenFlags = selectedCentralItems.map(({ isKitchen }) => isKitchen);
    const normalizedSearch = normalizeStringValue(search);

    return products.filter((product) => {
      const matchesSearch =
        !normalizedSearch ||
        normalizeStringValue(product.name || '').includes(normalizedSearch) ||
        normalizeStringValue(product.sku || '').includes(normalizedSearch);

      if (!matchesSearch) {
        return false;
      }

      const categoryId = product.category || NONE_FILTER_ID;

      if (!selectedCategoryIds.includes(categoryId)) {
        return false;
      }

      const subCategoryId = product.subCategory || NONE_FILTER_ID;

      if (!selectedSubCategoryIds.includes(subCategoryId)) {
        return false;
      }

      // The central-product filter only applies while its dropdown is offered.
      if (displayCentralFilter && !selectedKitchenFlags.includes(product.isKitchen)) {
        return false;
      }

      return true;
    });
  }, [
    products,
    search,
    selectedCategoryItems,
    selectedSubCategoryItems,
    selectedCentralItems,
    displayCentralFilter,
  ]);

  const listData = useMemo(
    () =>
      filteredProducts.map((product) => ({
        ...product,
        isRowSelected: selectedProductIds.includes(product.id),
      })),
    [filteredProducts, selectedProductIds],
  );

  const handleSelection = (selectedItems) => {
    const nextSelectedIds = mergeReportedSelection(
      selectedItems,
      filteredProducts.map(({ id }) => id),
      selectedProductIds,
    );

    if (haveSameIds(nextSelectedIds, selectedProductIds)) {
      return;
    }

    onSelectionChange(nextSelectedIds);
  };

  return (
    <Container>
      <ListView
        columns={columns}
        data={listData}
        defaultMaxPerPage={DEFAULT_MAX_PER_PAGE}
        isLoading={isLoading}
        padding={'6px 24px 0px 24px'}
        ref={listViewRef}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <HeaderContainer>
            <SearchBar
              placeholder={i18next.t('GENERAL.SEARCH')}
              setValue={setSearch}
              value={search}
            />
            <Dropdown
              iconSrc={CATEGORY_FILTER_ICON}
              isUniqueSelection={false}
              items={categoryItems}
              placeholder={i18next.t('GENERAL.CATEGORY')}
              selectedItems={selectedCategoryItems}
              isRequired
              onSelectionChange={setSelectedCategoryItems}
            />
            <Dropdown
              iconSrc={SUB_CATEGORY_FILTER_ICON}
              isUniqueSelection={false}
              items={subCategoryItems}
              placeholder={i18next.t('GENERAL.SUB_CATEGORY')}
              selectedItems={selectedSubCategoryItems}
              isRequired
              onSelectionChange={setSelectedSubCategoryItems}
            />
            {displayCentralFilter && (
              <Dropdown
                iconSrc={CENTRAL_FILTER_ICON}
                isUniqueSelection={false}
                items={centralFilterItems}
                placeholder={i18next.t('EXTERNAL_CLIENTS.CREATION.MANUAL_COLUMN_CENTRAL_PRODUCT')}
                selectedItems={selectedCentralItems}
                isRequired
                onSelectionChange={setSelectedCentralItems}
              />
            )}
          </HeaderContainer>
        )}
        setSelectedItems={handleSelection}
        forceEnableSelection
        hideSearchbar
      />
    </Container>
  );
};

ManualProductList.propTypes = {
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      isKitchen: PropTypes.bool,
    }),
  ).isRequired,
  externalClientId: PropTypes.string,
  hasCentralKitchens: PropTypes.bool,
  selectedProductIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

ManualProductList.defaultProps = {
  externalClientId: null,
  hasCentralKitchens: false,
};

export default ManualProductList;
